import qs from 'qs'
import moment from 'moment'
import oikosApi from '../../services/oikos'
import powerAppsApi from '../../services/powerapps'
import { notifyError, notifySuccess } from '../../utils/notify'
import { needsContrFornApproval, CONTR_FORN_STATUS_EM_APROVACAO, CONTR_FORN_STATUS_EM_ASSINATURA } from '../../components/Modals/ModalFormContrForn/contrFornApprovalRules'
import { fetchContrFornEventosWithBordero } from './contratosTabHelpers'

export const STATUS_ATIVO = 2
export const STATUS_ENCERRADO = 3
export const STATUS_EM_RENOVACAO = 10
const STATUS_FECHADO = new Set([3, 4, 9, 11])

const TIPOS = ['objeto', 'prazo', 'valor']

export const rotuloAditivo = (tp) => {
  const map = { prazo: 'Prazo', valor: 'Valor', objeto: 'Objeto' }
  const partes = String(tp || '').split('_').filter((parte) => map[parte])
  return partes.map((parte) => map[parte]).join(' e ') || 'Aditivo'
}

export const aditivoAberto = (aditivos) => (
  (aditivos || []).find((aditivo) => !STATUS_FECHADO.has(Number(aditivo.contrFornStatus_id)))
)

export const agruparAditivos = (rows) => {
  const lista = Array.isArray(rows) ? rows : []
  const filhos = lista.filter((row) => Number(row.contrForn_original))
  return lista
    .filter((row) => !Number(row.contrForn_original))
    .map((pai) => ({
      ...pai,
      aditivos: filhos.filter((filho) => Number(filho.contrForn_original) === Number(pai.contrForn_id)),
    }))
}

const centavos = (valor) => Math.round((Number(valor) || 0) * 100)

const somaCentavos = (parcelas) => parcelas.reduce((acc, parcela) => acc + centavos(parcela.contrFornEvento_vlr), 0)

export const parcelaTravada = (parcela) => Boolean(parcela?.bdro_id || parcela?.bdro_dtQuit)

const tipoCodigo = (flags) => TIPOS.filter((tipo) => flags[tipo]).join('_')

const dentroDaVigencia = (data, inicio, fim) => {
  if (!data || !inicio || !fim) return false
  const dia = moment(data, 'YYYY-MM-DD', true)
  const ini = moment(inicio, 'YYYY-MM-DD', true)
  const end = moment(fim, 'YYYY-MM-DD', true)
  if (!dia.isValid() || !ini.isValid() || !end.isValid()) return false
  return !dia.isBefore(ini, 'day') && !dia.isAfter(end, 'day')
}

const mesesDoDelta = (atuais, propostas) => {
  const somar = (lista) => {
    const meses = {}
    lista.forEach((parcela) => {
      const mes = String(parcela.contrFornEvento_dtPrev || '').slice(0, 7)
      if (!/^\d{4}-\d{2}$/.test(mes)) return
      meses[mes] = (meses[mes] || 0) + (Number(parcela.contrFornEvento_vlr) || 0)
    })
    return meses
  }
  const antes = somar(atuais)
  const depois = somar(propostas)
  const meses = new Set([...Object.keys(antes), ...Object.keys(depois)])
  const delta = []
  meses.forEach((mes) => {
    const diferenca = (depois[mes] || 0) - (antes[mes] || 0)
    if (diferenca > 0.009) delta.push({ mes, diferenca })
  })
  return delta
}

async function saldoCobreDelta(orcItemId, atuais, propostas) {
  const aumentos = mesesDoDelta(atuais, propostas)
  if (!aumentos.length) return true

  let res
  try {
    res = await powerAppsApi.post('/', {
    acao: 'verificar_disponibilidade_orcItem',
    orcItem_id: orcItemId,
  }, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Accept: 'multipart/form-data',
    },
  })

  const limites = Array.isArray(res?.data) ? res.data : []
  const estourado = aumentos.find((item) => {
    const limite = limites.find((row) => String(row.mes || '').slice(0, 7) === item.mes)
    return !limite || item.diferenca - Number(limite.disponivel || 0) > 0.009
  })
  if (estourado) {
    notifyError(`Saldo insuficiente em ${moment(estourado.mes, 'YYYY-MM').format('MM/YYYY')}.`)
    return false
  }
  return true
  } catch (error) {
    console.error(error)
    notifyError('Não foi possível conferir o saldo orçamentário.')
    return false
  }
}

const CAMPOS_COPIADOS = [
  'contrForn_parteTab',
  'contrForn_parteId',
  'contrForn_desc',
  'contrForn_obj',
  'contrForn_vlrGlobal',
  'contrForn_dtAssin',
  'contrForn_dtVigIni',
  'contrForn_dtVigFim',
  'orcItem_id',
  'contrFornTp_id',
  'pf_id',
  'contrForn_detalhamento',
  'tpMinuta_id',
  'contrForn_dtPgtoFixa',
  'contrForn_vlrFixo',
  'contrForn_renovacaoAutomatica',
  'contrForn_maoObraLocal',
  'contrForn_contratante',
  'meioPgto_id',
  'conta_id',
  'formaPgto_id',
  'contrForn_continuo',
  'compraCotacao_id',
  'pixTp_id',
  'contrForn_chavePix',
  'contrForn_pgtoCompetModo',
  'minutaMod_id',
]

async function buscarContrato(contrFornId) {
  const res = await oikosApi.get(`/?${qs.stringify({
    action: 'get',
    table: 'contrForn',
    data: { contrForn_id: contrFornId, contrForn_ativo: 1 },
  })}`)
  return res?.data?.[0] || null
}

async function buscarFilhos(contrFornId) {
  const res = await oikosApi.get(`/?${qs.stringify({
    action: 'get',
    table: 'contrForn',
    data: { contrForn_original: contrFornId, contrForn_ativo: 1 },
  })}`)
  return res?.data || []
}

async function desfazerAditivo(childId, parentId) {
  try {
    const eventos = await oikosApi.get(`/?${qs.stringify({
      action: 'get',
      table: 'contrFornEvento',
      data: { contrForn_id: childId, contrFornEvento_ativo: 1 },
      fields: { contrFornEvento_id: 'contrFornEvento_id' },
    })}`)
    await Promise.all((eventos?.data || []).map((evento) => oikosApi.post('/', {
      action: 'update',
      table: 'contrFornEvento',
      data: { contrFornEvento_id: evento.contrFornEvento_id, contrFornEvento_ativo: 0 },
    })))
    await oikosApi.post('/', {
      action: 'update',
      table: 'contrForn',
      data: { contrForn_id: childId, contrForn_ativo: 0, contrFornStatus_id: STATUS_ENCERRADO },
    })
    if (parentId) {
      await oikosApi.post('/', {
        action: 'update',
        table: 'contrForn',
        data: { contrForn_id: parentId, contrFornStatus_id: STATUS_ATIVO },
      })
    }
  } catch (error) {
    console.error('Falha ao desfazer aditivo incompleto:', error)
  }
}

export async function criarAditivo({ contrato, flags, motivo, dtVigFim, vlrGlobal, objeto, parcelas, user }) {
  const tipo = tipoCodigo(flags)
  if (!tipo) {
    notifyError('Escolha o que muda: prazo, valor ou objeto.')
    return false
  }
  const motivoLimpo = String(motivo || '').trim()
  if (!motivoLimpo) {
    notifyError('Informe o motivo do aditivo.')
    return false
  }

  const pai = await buscarContrato(contrato.contrForn_id)
  if (!pai || Number(pai.contrFornStatus_id) !== STATUS_ATIVO) {
    notifyError('Só é possível aditar um contrato ativo.')
    return false
  }
  const filhos = await buscarFilhos(pai.contrForn_id)
  if (aditivoAberto(filhos)) {
    notifyError('Este contrato já tem um aditivo em andamento.')
    return false
  }

  const inicio = String(pai.contrForn_dtVigIni || '').slice(0, 10)
  const fimAtual = String(pai.contrForn_dtVigFim || '').slice(0, 10)
  const fim = flags.prazo ? String(dtVigFim || '').slice(0, 10) : fimAtual
  if (flags.prazo) {
    if (!moment(fim, 'YYYY-MM-DD', true).isValid() || moment(fim).isBefore(moment(inicio), 'day')) {
      notifyError('A nova data final precisa ser igual ou posterior ao início da vigência.')
      return false
    }
  }

  const eventosServidor = await fetchContrFornEventosWithBordero(oikosApi, pai.contrForn_id)
  const travadas = eventosServidor.filter(parcelaTravada)
  const idsTravados = new Set(travadas.map((parcela) => Number(parcela.contrFornEvento_id)))
  const editaveis = (parcelas || []).filter((parcela) => !idsTravados.has(Number(parcela.contrFornEvento_id)))
  const ultimaTravada = travadas
    .map((parcela) => String(parcela.contrFornEvento_dtPrev || parcela.bdro_dtQuit || '').slice(0, 10))
    .filter((data) => moment(data, 'YYYY-MM-DD', true).isValid())
    .sort()
    .pop()
  if (flags.prazo && ultimaTravada && moment(fim).isBefore(moment(ultimaTravada), 'day')) {
    notifyError('A nova data final não pode ser anterior a uma parcela já lançada ou paga.')
    return false
  }

  let valorNovo = Number(pai.contrForn_vlrGlobal) || 0
  let parcelasResultado = eventosServidor
  if (flags.valor) {
    valorNovo = Number(vlrGlobal)
    if (!Number.isFinite(valorNovo) || valorNovo < 0) {
      notifyError('Informe o novo valor global.')
      return false
    }
    if (centavos(valorNovo) < somaCentavos(travadas)) {
      notifyError('O novo valor não pode ser menor que a soma das parcelas já lançadas ou pagas.')
      return false
    }
    parcelasResultado = [...travadas, ...editaveis]
    if (somaCentavos(parcelasResultado) !== centavos(valorNovo)) {
      notifyError('A soma das parcelas deve ser igual ao novo valor global.')
      return false
    }
    const semData = parcelasResultado.find((parcela) => !dentroDaVigencia(String(parcela.contrFornEvento_dtPrev || '').slice(0, 10), inicio, fim))
    if (semData) {
      notifyError('Toda parcela precisa ter data prevista dentro da vigência.')
      return false
    }
    const semDescricao = editaveis.find((parcela) => !String(parcela.contrFornEvento_desc || '').trim())
    if (semDescricao) {
      notifyError('Toda parcela nova ou ajustada precisa de descrição.')
      return false
    }
    const saldoOk = await saldoCobreDelta(pai.orcItem_id, eventosServidor, parcelasResultado)
    if (!saldoOk) return false
  } else if (flags.prazo) {
    const fora = eventosServidor.find((parcela) => !dentroDaVigencia(String(parcela.contrFornEvento_dtPrev || '').slice(0, 10), inicio, fim))
    if (fora) {
      notifyError('Há parcela fora da nova vigência. Inclua alteração de valor para ajustá-la.')
      return false
    }
  }

  const objetoNovo = flags.objeto ? String(objeto || '').trim() : pai.contrForn_obj
  if (flags.objeto && !objetoNovo) {
    notifyError('Informe o novo objeto.')
    return false
  }

  const sequencia = filhos.length + 1
  const numero = `${pai.contrForn_num}-AD${sequencia}`
  const valorParaAprovacao = flags.valor ? valorNovo : (Number(pai.contrForn_vlrGlobal) || 0)
  const exigeAprovacao = needsContrFornApproval(valorParaAprovacao)
  const statusInicial = exigeAprovacao ? CONTR_FORN_STATUS_EM_APROVACAO : CONTR_FORN_STATUS_EM_ASSINATURA

  const data = { contrForn_ativo: 1 }
  CAMPOS_COPIADOS.forEach((campo) => {
    if (pai[campo] !== undefined && pai[campo] !== null && pai[campo] !== '') data[campo] = pai[campo]
  })
  Object.assign(data, {
    contrForn_num: numero,
    contrForn_original: pai.contrForn_id,
    contrForn_aditivoTp: tipo,
    contrForn_obs: motivoLimpo,
    contrForn_obj: objetoNovo,
    contrForn_vlrGlobal: flags.valor ? valorNovo : pai.contrForn_vlrGlobal,
    contrForn_dtVigFim: fim,
    contrFornStatus_id: statusInicial,
    contrForn_usuarioCria: user?.pf_id,
    contrForn_usuarioMod: user?.pf_id,
    pf_id: pai.pf_id || user?.pf_id,
  })

  let childId = null
  try {
    const criado = await oikosApi.post('/', { action: 'insert', table: 'contrForn', data })
    childId = criado?.data?.[0]?.contrForn_id
    if (!childId) throw new Error('insert sem id')

    if (flags.valor) {
      for (const parcela of editaveis) {
        const origem = Number(parcela.contrFornEvento_id) || 0
        await oikosApi.post('/', {
          action: 'insert',
          table: 'contrFornEvento',
          data: {
            contrForn_id: childId,
            ...(origem ? { contrFornEvento_origem: origem } : {}),
            contrFornEvento_num: parcela.contrFornEvento_num || undefined,
            contrFornEvento_desc: String(parcela.contrFornEvento_desc || '').trim(),
            contrFornEvento_pgtoPrazoDias: parcela.contrFornEvento_pgtoPrazoDias || undefined,
            contrFornEvento_dtPrev: String(parcela.contrFornEvento_dtPrev).slice(0, 10),
            contrFornEvento_vlr: Number(Number(parcela.contrFornEvento_vlr || 0).toFixed(2)),
            contrFornEvento_aprovNecessita: 1,
            contrFornEvento_aprovLiberado: 0,
            contrFornEvento_usuarioCria: user?.pf_id,
            contrFornEvento_usuarioMod: user?.pf_id,
            contrFornEvento_ativo: 1,
            categ_id: parcela.categ_id || undefined,
            contrFornEvento_liberacaoAutomatica: parcela.contrFornEvento_liberacaoAutomatica ? 1 : 0,
            contrFornEvento_dtPgtoFixa: parcela.contrFornEvento_dtPgtoFixa ?? pai.contrForn_dtPgtoFixa ?? 0,
            contrFornEvento_vlrFixo: parcela.contrFornEvento_vlrFixo ?? 0,
          },
        })
      }
    }

    await oikosApi.post('/', {
      action: 'update',
      table: 'contrForn',
      data: { contrForn_id: pai.contrForn_id, contrFornStatus_id: STATUS_EM_RENOVACAO },
    })

    if (exigeAprovacao) {
      const centro = Number(contrato.centroDeCustoRubr_id || 0)
      const pfDestino = [13, 14].includes(centro) ? 2 : 1
      const tarefa = {
        action: 'insert',
        table: 'task',
        data: {
          task_tabRef: 'contrForn',
          task_tabId: childId,
          pf_id: user?.pf_id,
          task_prazo: moment().add(3, 'days').format('YYYY-MM-DD'),
          taskTp_id: 3,
          task_desc: `Aprovar aditivo ${numero}`,
          task_orientacao: 'Avalie o aditivo. Após, clique em "Aprovar" ou "Rejeitar". O contrato original só muda depois da assinatura.',
          task_usuarioCria: user?.pf_id,
        },
      }
      const responseTask = await oikosApi.post('/', tarefa)
      const taskMae = responseTask?.data?.[0]?.task_id
      if (taskMae) {
        await oikosApi.post('/', {
          ...tarefa,
          data: { ...tarefa.data, pf_id: pfDestino, task_mae: taskMae },
        })
      }
    }

    notifySuccess(exigeAprovacao
      ? 'Aditivo enviado para aprovação.'
      : 'Aditivo criado. Anexe o documento assinado para aplicá-lo ao contrato.')
    return true
  } catch (error) {
    console.error(error)
    if (childId) await desfazerAditivo(childId, pai.contrForn_id)
    notifyError('Não foi possível criar o aditivo.')
    return false
  }
}

export async function restaurarContratoPaiDoAditivo(contrFornId) {
  try {
    const filho = await buscarContrato(contrFornId)
    const paiId = Number(filho?.contrForn_original) || 0
    if (!paiId) return
    const filhos = await buscarFilhos(paiId)
    if (aditivoAberto(filhos.filter((item) => Number(item.contrForn_id) !== Number(contrFornId)))) return
    await oikosApi.post('/', {
      action: 'update',
      table: 'contrForn',
      data: { contrForn_id: paiId, contrFornStatus_id: STATUS_ATIVO },
    })
  } catch (error) {
    console.error(error)
    notifyError('O aditivo foi rejeitado, mas o contrato original não voltou para Ativo.')
  }
}

